import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { APP_VALUES } from '@/utils/apps';

/**
 * AppScopeContext — the currently selected product scope (CRM / HRMS /
 * TrackDox / all) shared across the billing and platform surfaces.
 *
 * Persisted in sessionStorage so the selection survives reloads and carries
 * through a Services → Plans → Subscriptions workflow.
 */

const STORAGE_KEY = 'ac_appScope';
const DEFAULT_SCOPE = 'all';

const AppScopeContext = createContext(null);

export const useAppScope = () => {
  const ctx = useContext(AppScopeContext);
  if (!ctx) {
    throw new Error('useAppScope must be used within an AppScopeProvider');
  }
  return ctx;
};

const readInitialScope = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved === DEFAULT_SCOPE || APP_VALUES.includes(saved)) return saved;
  } catch {
    /* sessionStorage unavailable (SSR / privacy mode) — fall through */
  }
  return DEFAULT_SCOPE;
};

export const AppScopeProvider = ({ children }) => {
  const [appScope, setAppScopeState] = useState(readInitialScope);

  const setAppScope = useCallback((next) => {
    const value = next === DEFAULT_SCOPE || APP_VALUES.includes(next) ? next : DEFAULT_SCOPE;
    setAppScopeState(value);
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, appScope);
    } catch {
      /* ignore persistence failures */
    }
  }, [appScope]);

  /** True when a specific app is selected (not the "all" meta-scope). */
  const isScoped = appScope !== DEFAULT_SCOPE;

  /**
   * Filter a list of records that carry an `app` field by the current scope.
   * In "all" mode the list is returned unchanged.
   */
  const filterByScope = useCallback(
    (records, getApp = (r) => r?.app) =>
      appScope === DEFAULT_SCOPE
        ? records
        : (records || []).filter((r) => getApp(r) === appScope),
    [appScope]
  );

  return (
    <AppScopeContext.Provider value={{ appScope, setAppScope, isScoped, filterByScope }}>
      {children}
    </AppScopeContext.Provider>
  );
};

export default AppScopeContext;
